import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import { DateFormatter } from '@repo/toolkit/date'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import Box from '@repo/ui/box'
import Button from '@repo/ui/button'
import { Column } from '@repo/ui/data-table'
import { CheckListTwoIcon, GoalIcon } from '@repo/ui/icons'
import Modal, { MODAL_CONTENT_CLASSNAME } from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useState } from 'react'

import { API, constructApiUrl } from '../../api/apis.config'
import DynamicDataTable from '../../components/elements/dynamic-table'
import UserNavBar from '../../components/elements/nav-bar'
import MissionToStaffReportView from '../../components/pages/mission-to-staff-report/mission-to-staff-report-view'
import { Paths } from '../../lib/constants/path'
import useAuth from '../../lib/hooks/use-auth'
import { useDownloadDocuments } from '../../lib/hooks/use-download-documents'
import { useModalManager } from '../../lib/hooks/use-modal-manager'
import useTranslate from '../../lib/hooks/use-translate'
import { MissionToStaffMission } from '../../lib/types/models/mission-to-staff/mission-to-staff'

type MissionPdfButtonProps = {
  tripFolderId?: number
}

const MissionPdfButton = ({ tripFolderId }: MissionPdfButtonProps) => {
  const { t } = useTranslate()
  const [loading, setLoading] = useState(false)
  const { downloadDocuments } = useDownloadDocuments()

  if (!tripFolderId) return '-'

  return (
    <Button
      variant="ghost"
      label={t('staff.personal.space.missions.instructions.print.pdf')}
      loading={loading}
      onClick={async () => {
        setLoading(true)
        await downloadDocuments({
          endpoint: constructApiUrl(API.tripFolder.generatePdf.url, { id: tripFolderId })
        })
        setLoading(false)
      }}
    />
  )
}

export function StaffMissionsPage() {
  const { t } = useTranslate()
  const { user } = useAuth()
  const { navigate } = useNavigator()
  const { modalState, selectedData, toggleModal, closeModel } = useModalManager<
    'view-report',
    number
  >(['view-report'])

  const columns: Column<MissionToStaffMission>[] = [
    {
      accessorKey: 'mission.date',
      enableSorting: true,
      header: t('staff.personal.space.missions.date'),
      cell: ({ row }) => <Badge text={DateFormatter.DDMMYYYY(row.original.mission.date)} />
    },
    {
      accessorKey: 'mission.title',
      enableSorting: true,
      header: t('staff.personal.space.missions.title')
    },
    {
      accessorKey: 'mission.meetingPlace.label',
      header: t('staff.personal.space.missions.meeting.place'),
      cell: ({ row }) => valueRender(row.original.mission.meetingPlace?.label)
    },
    {
      accessorKey: 'mission.appointmentTime',
      header: t('staff.personal.space.missions.appointment.time'),
      cell: ({ row }) =>
        row.original.mission.appointmentTime ? (
          <Badge
            text={DateFormatter.formatTimeString(
              row.original.mission.appointmentTime.hours,
              row.original.mission.appointmentTime.minutes
            )}
            variant="grey"
          />
        ) : (
          '-'
        )
    },
    {
      accessorKey: 'mission.id',
      header: t('staff.personal.space.missions.instructions'),
      cell: ({ row }) => <MissionPdfButton tripFolderId={row.original.mission.tripFolderId} />
    },
    {
      accessorKey: 'mission.report.id',
      header: t('staff.personal.space.missions.repport'),
      cell: ({ row }) => {
        const text = !!row.original.report
          ? t('staff.personal.space.missions.repport.view')
          : t('staff.personal.space.missions.repport.complete')
        return (
          <Button
            label={text}
            variant={!!row.original.report ? 'success' : 'orange'}
            onClick={() => {
              if (!row.original.report)
                navigate(
                  Paths.STAFF_PERSONAL_SPACE_MISSION_REPORT.replace(':id', String(row.original.id))
                )
              else toggleModal('view-report', row.original.id)
            }}
          />
        )
      }
    },
    {
      accessorKey: 'travelExpensesStatus',
      header: t('staff.personal.space.missions.notes'),
      cell: ({ row }) => {
        const access = row.original.travelExpensesSavedAt || row.original.travelExpensesSentAt
        return (
          <Button
            variant={access ? 'ghost' : 'primary'}
            label={
              access
                ? t('staff.personal.space.missions.instructions.notes.access')
                : t('staff.personal.space.missions.instructions.notes.add')
            }
            onClick={() => {
              navigate(
                Paths.STAFF_PERSONAL_SPACE_MISSION_TRAVEL_EXPENSES.replace(
                  ':id',
                  String(row.original.id)
                )
              )
            }}
          />
        )
      }
    }
  ]

  const staffId = user?.staff?.id
  if (!staffId) return null

  return (
    <div className="flex flex-col gap-5">
      <UserNavBar
        title={t('staff.personal.space.missions')}
        icon={(props) => <GoalIcon {...props} color={tailwindColors.fushia.DEFAULT} />}
      />

      <Box className="px-5 py-6">
        <Typography variant="h6">{t('staff.personal.space.missions')}</Typography>
        <div className="h-4" />

        <DynamicDataTable<MissionToStaffMission>
          columns={columns}
          headerType={'none'}
          endpoint={constructApiUrl(API.staffs.getStaffMissions.url, { id: staffId })}
          paginated={false}
        />
      </Box>

      {selectedData && (
        <Modal
          isOpen={modalState['view-report']}
          title={t('staff.personal.space.missions.report.title')}
          icon={CheckListTwoIcon}
          size="medium"
          hideActions
          onCancel={() => closeModel('view-report')}
        >
          <div className="w-full divide-y divide-white-active">
            <MissionToStaffReportView
              missionToStaffId={selectedData}
              className={MODAL_CONTENT_CLASSNAME}
            />
            <div className="flex justify-end p-6">
              <Button
                label={t('button.close')}
                variant="ghost"
                onClick={() => closeModel('view-report')}
              />
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
